import React from 'react';
import PropTypes from 'prop-types';
import { Button, Icon, Loading, Modal, ModalFooter } from '@folio/stripes/components';
import { FormattedMessage, useIntl } from 'react-intl';
import { EXEC_STAGE } from '../../hooks/useLastExecution';
import { getLocalizedValue } from '../../helpers/localizationUtils';

function RunNowModal({ open, onClose, schedule, stage, execution, elapsed, onViewHistory }) {
  const intl = useIntl();
  const { locale } = intl;
  const scheduleName = getLocalizedValue(schedule.name, locale) || schedule.name || '';

  const recipientCount  = schedule.recipients?.length || 0;
  const recipientEmails = (schedule.recipients || []).map(r => r.email).filter(Boolean).join(', ');

  return (
    <Modal
      open={open}
      onClose={onClose}
      label={intl.formatMessage(
        { id: 'ui-reports.runNow.modal.title', defaultMessage: 'Run Now: {name}' },
        { name: scheduleName },
      )}
      footer={
        <ModalFooter>
          {(stage === EXEC_STAGE.SUCCESS || stage === EXEC_STAGE.FAILED) && (
            <Button
              buttonStyle="default"
              marginBottom0
              onClick={onViewHistory}
            >
              <Icon icon="eye-open">
                <FormattedMessage id="ui-reports.runNow.modal.viewHistoryBtn" defaultMessage="View Full History" />
              </Icon>
            </Button>
          )}
          <Button
            buttonStyle={stage === EXEC_STAGE.RUNNING ? 'default' : 'primary'}
            onClick={onClose}
            marginBottom0
          >
            <FormattedMessage id="ui-reports.button.close" defaultMessage="Close" />
          </Button>
        </ModalFooter>
      }
      size="medium"
    >
      <div style={{ padding: '1rem 0' }}>

        {/* ── RUNNING ── */}
        {stage === EXEC_STAGE.RUNNING && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem', padding: '1.5rem 0' }}>
            <Loading />
            <p style={{ margin: 0, fontWeight: 'bold' }}>
              <FormattedMessage id="ui-reports.runNow.modal.running.title" defaultMessage="Executing report..." />
            </p>
            <p style={{ margin: 0, color: 'var(--color-text-secondary, #666)', textAlign: 'center' }}>
              <FormattedMessage
                id="ui-reports.runNow.modal.running.desc"
                defaultMessage="The report is being executed in the background. This may take a few minutes depending on data volume."
              />
            </p>
            <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--color-text-secondary, #888)' }}>
              <FormattedMessage
                id="ui-reports.runNow.modal.running.elapsed"
                defaultMessage="Elapsed: {seconds}s"
                values={{ seconds: elapsed }}
              />
            </p>
          </div>
        )}

        {/* ── SUCCESS ── */}
        {stage === EXEC_STAGE.SUCCESS && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--color-success, #198754)' }}>
              <Icon icon="check-circle" size="large" />
              <span style={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                <FormattedMessage id="ui-reports.runNow.modal.success.title" defaultMessage="Report executed successfully" />
              </span>
            </div>
            <hr style={{ margin: '0.5rem 0', borderColor: 'var(--color-border, #ddd)' }} />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem 1rem', fontSize: '0.9rem' }}>
              <span style={{ color: 'var(--color-text-secondary, #666)' }}>
                <FormattedMessage id="ui-reports.runNow.modal.result.records" defaultMessage="Records exported:" />
              </span>
              <span style={{ fontWeight: 'bold' }}>{execution?.recordsCount ?? '-'}</span>

              <span style={{ color: 'var(--color-text-secondary, #666)' }}>
                <FormattedMessage id="ui-reports.runNow.modal.result.duration" defaultMessage="Duration:" />
              </span>
              <span style={{ fontWeight: 'bold' }}>
                {execution?.duration ? `${Math.floor(execution.duration / 1000)}s` : '-'}
              </span>

              <span style={{ color: 'var(--color-text-secondary, #666)' }}>
                <FormattedMessage id="ui-reports.runNow.modal.result.format" defaultMessage="Output format:" />
              </span>
              <span style={{ fontWeight: 'bold', textTransform: 'uppercase' }}>{schedule.outputFormat || '-'}</span>
            </div>
            <hr style={{ margin: '0.5rem 0', borderColor: 'var(--color-border, #ddd)' }} />

            {/* Email status — من الـ execution الفعلي */}
            {recipientCount > 0 ? (
              <div style={{
                display: 'flex', alignItems: 'flex-start', gap: '0.5rem',
                padding: '0.75rem',
                background: execution?.emailSent
                  ? 'var(--color-fill-accent-subtle, #e8f4fd)'
                  : 'var(--color-fill-error-subtle, #fdecea)',
                borderRadius: '4px',
              }}>
                <Icon icon={execution?.emailSent ? 'envelope' : 'exclamation-circle'} />
                <div>
                  <p style={{ margin: '0 0 0.25rem', fontWeight: 'bold', fontSize: '0.9rem' }}>
                    {execution?.emailSent ? (
                      <FormattedMessage
                        id="ui-reports.runNow.modal.email.sent"
                        defaultMessage="Email sent to {count} {count, plural, one {recipient} other {recipients}}"
                        values={{ count: recipientCount }}
                      />
                    ) : (
                      <FormattedMessage
                        id="ui-reports.runNow.modal.email.failed"
                        defaultMessage="Email could not be sent"
                      />
                    )}
                  </p>
                  <p style={{ margin: 0, fontSize: '0.8rem', color: 'var(--color-text-secondary, #666)', wordBreak: 'break-word' }}>
                    {recipientEmails}
                  </p>
                </div>
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem', background: 'var(--color-fill-subtle, #f8f8f8)', borderRadius: '4px' }}>
                <Icon icon="envelope" />
                <span style={{ fontSize: '0.9rem', color: 'var(--color-text-secondary, #666)' }}>
                  <FormattedMessage id="ui-reports.runNow.modal.email.noRecipients" defaultMessage="No email recipients configured for this schedule" />
                </span>
              </div>
            )}
          </div>
        )}

        {/* ── FAILED ── */}
        {stage === EXEC_STAGE.FAILED && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--color-error, #dc3545)' }}>
              <Icon icon="times-circle" size="large" />
              <span style={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                <FormattedMessage id="ui-reports.runNow.modal.failed.title" defaultMessage="Report execution failed" />
              </span>
            </div>
            {execution?.errorMessage && (
              <div style={{ padding: '0.75rem', background: 'var(--color-fill-error-subtle, #fdecea)', borderRadius: '4px', borderLeft: '3px solid var(--color-error, #dc3545)' }}>
                <p style={{ margin: '0 0 0.25rem', fontWeight: 'bold', fontSize: '0.85rem' }}>
                  <FormattedMessage id="ui-reports.runNow.modal.failed.errorLabel" defaultMessage="Error details:" />
                </p>
                <p style={{ margin: 0, fontSize: '0.85rem', wordBreak: 'break-word' }}>{execution.errorMessage}</p>
              </div>
            )}
          </div>
        )}

        {/* ── TIMEOUT ── */}
        {stage === EXEC_STAGE.TIMEOUT && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--color-warning, #ffc107)' }}>
              <Icon icon="clock" size="large" />
              <span style={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                <FormattedMessage id="ui-reports.runNow.modal.timeout.title" defaultMessage="Execution is taking longer than expected" />
              </span>
            </div>
            <p style={{ margin: 0, color: 'var(--color-text-secondary, #666)' }}>
              <FormattedMessage
                id="ui-reports.runNow.modal.timeout.desc"
                defaultMessage="The report is still running in the background. You can close this dialog and check the execution history later."
              />
            </p>
          </div>
        )}

      </div>
    </Modal>
  );
}

RunNowModal.propTypes = {
  open:           PropTypes.bool.isRequired,
  onClose:        PropTypes.func.isRequired,
  onViewHistory:  PropTypes.func.isRequired,
  schedule:       PropTypes.object.isRequired,
  stage:          PropTypes.string.isRequired,
  execution:      PropTypes.object,
  elapsed:        PropTypes.number,
};

RunNowModal.defaultProps = { elapsed: 0 };

export default RunNowModal;
